import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { SearchField } from "@react-spectrum/s2";
import { useTranslation } from 'react-i18next';
import { OrgUserEntry } from '../../types';
import { fetchOrgUsers } from '../../services/api';
import { formatRelativeTime } from '../../utils/dateUtils';
import { UserRolesCell } from './UserRolesCell';
import { colorForUser, initialsForUser } from '../../utils/avatar';
import { appendUniqueBy } from '../../utils/appendUnique';
import { ActionsColumnHeader, DataTable } from '../common';
import type { ColumnDefinition } from '../common';
import '../common/DomainPageShell.css';
import './ManageUsersPanel.css';
export interface OrgUsersTableHandle {
    /** Re-fetch the current page/search without resetting them. */ reload: () => void;
}
export interface OrgUsersTableProps {
    orgId: string;
    pageSize?: number;
    /** Omit this user_id from every tab, count, and page (e.g. the current org admin). */ excludeUserId?: string;
    /** Actions column content for a row. Omit edit/delete here for read-only contexts. */ renderActions: (user: OrgUserEntry) => React.ReactNode;
    /** Optional footnote rendered under the pagination bar. */ footnote?: React.ReactNode;
    /** Right-aligned table toolbar content (e.g. an "Add user" ActionButton). */ toolbarEnd?: React.ReactNode;
}
/**
 * The "All users" browse experience (search, avatar/status/last-active
 * table, infinite scroll)
 * factored out of the Security > Users page so other surfaces -- e.g. the
 * Command Center's Org Admin assignment list -- can show the identical view
 * with a different actions column, without duplicating the fetch/paginate/
 * filter wiring. Deliberately excludes ManageUsersPanel's inline role editor:
 * this is a read-plus-custom-action browser, not a role-management one.
 */ export const OrgUsersTable = forwardRef<OrgUsersTableHandle, OrgUsersTableProps>(({ orgId, pageSize = 5, excludeUserId, renderActions, footnote, toolbarEnd }, ref)=>{
    const { t } = useTranslation();
    const [pagedUsers, setPagedUsers] = useState<OrgUserEntry[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    // Synchronous guard: onLoadMore can fire repeatedly before `loadingMore` commits.
    const loadMoreInFlightRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const hasMore = pagedUsers.length < totalUsers;
    useEffect(()=>{
        const timer = setTimeout(()=>setDebouncedSearch(searchQuery.trim()), 300);
        return ()=>clearTimeout(timer);
    }, [
        searchQuery
    ]);
    // Fetch a single page. `replace` resets the accumulated list (first page,
    // or a search/org change); `append` grows it for infinite scroll.
    const loadPage = useCallback(async (pageToLoad: number, mode: 'replace' | 'append')=>{
        if (mode === 'replace') setLoading(true);
        else setLoadingMore(true);
        setError(null);
        try {
            const result = await fetchOrgUsers(orgId, {
                page: pageToLoad,
                pageSize,
                search: debouncedSearch || undefined,
                excludeUserId
            });
            setPagedUsers((prev)=>mode === 'replace' ? result.users : appendUniqueBy(prev, result.users, (u)=>u.user_id));
            setTotalUsers(result.total);
            setPage(pageToLoad);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manageUsers.errors.loadUsers'));
        } finally{
            if (mode === 'replace') setLoading(false);
            else {
                loadMoreInFlightRef.current = false;
                setLoadingMore(false);
            }
        }
    }, [
        orgId,
        pageSize,
        debouncedSearch,
        excludeUserId,
        t
    ]);
    // Reload from the first page whenever the org or search changes.
    useEffect(()=>{
        loadPage(1, 'replace');
    }, [
        loadPage
    ]);
    const loadMore = useCallback(()=>{
        if (loading || loadingMore || !hasMore || loadMoreInFlightRef.current) return;
        loadMoreInFlightRef.current = true;
        loadPage(page + 1, 'append');
    }, [
        loading,
        loadingMore,
        hasMore,
        page,
        loadPage
    ]);
    useImperativeHandle(ref, ()=>({
            reload: ()=>loadPage(1, 'replace')
        }), [
        loadPage
    ]);
    const userColumns: ColumnDefinition<OrgUserEntry>[] = useMemo(()=>[
            {
                key: 'name',
                label: t('manageUsers.usersColumns.name'),
                minWidth: 180,
                allowsResizing: true,
                isRowHeader: true,
                pinned: true,
                align: 'start',
                render: (user)=>(<div className={" sd171 Ug171 qg171 eb171"}>
              <div className="manage-users-avatar" style={{
                        background: colorForUser(user.user_id)
                    }} aria-hidden="true">
                {initialsForUser(user.display_name, user.user_id)}
              </div>
              <span className="manage-users-primary-label" title={user.display_name || undefined}>
                {user.display_name || '—'}
              </span>
              {user.is_org_admin && (<span className="manage-users-creator-badge">{t('manageUsers.badges.orgAdmin')}</span>)}
            </div>)
            },
            {
                key: 'email',
                label: t('manageUsers.usersColumns.email'),
                minWidth: 200,
                allowsResizing: true,
                align: 'start',
                render: (user)=>(<span className="manage-users-audit-cell" title={user.user_id}>
              {user.user_id}
            </span>)
            },
            {
                key: 'access',
                label: t('manageUsers.usersColumns.access'),
                minWidth: 140,
                allowsResizing: true,
                align: 'start',
                render: (user)=>(<span className={`manage-users-status-badge ${user.has_read_access ? 'manage-users-status-badge--authorized' : 'manage-users-status-badge--pending'}`}>
              {user.has_read_access ? t('manageUsers.accessStatus.authorized') : t('manageUsers.accessStatus.pending')}
            </span>)
            },
            {
                key: 'roles',
                label: t('manageUsers.usersColumns.roles'),
                minWidth: 120,
                allowsResizing: true,
                defaultVisible: false,
                align: 'start',
                render: (user)=><UserRolesCell user={user}/>
            },
            {
                key: 'last_active',
                label: t('manageUsers.usersColumns.lastActive'),
                minWidth: 120,
                allowsResizing: true,
                align: 'start',
                render: (user)=>(<span className="manage-users-audit-cell">
              {user.last_seen_at ? formatRelativeTime(user.last_seen_at) : '—'}
            </span>)
            },
            {
                key: 'actions',
                label: t('manageUsers.usersColumns.actions'),
                minWidth: 120,
                excludeFromConfigurator: true,
                renderHeader: ()=><ActionsColumnHeader/>,
                render: (user)=>(<div className={"manage-users-row-actions" + " sd171 Ub171 qb171 eb171"}>
              {renderActions(user)}
            </div>)
            }
        ], [
        renderActions,
        t
    ]);
    const emptyDescription = debouncedSearch ? t('manageUsers.usersTable.emptySearch') : t('manageUsers.usersTable.emptyAll');
    return (<div className={"manage-users-fill" + " _ub171 _vb171 _sb171 Ma171"}>
        {error && <div className="manage-users-error-banner">{error}</div>}

        <div className="manage-users-table-region">
          <DataTable<OrgUserEntry> items={pagedUsers} columns={userColumns} getRowKey={(user)=>user.user_id} ariaLabel={t('manageUsers.usersTable.ariaLabel')} loading={loading} density="regular" loadingState={loadingMore ? 'loadingMore' : 'idle'} onLoadMore={hasMore ? loadMore : undefined} layout="fill" configurableColumns firstColumnDivider toolbarStart={(<SearchField aria-label={t('manageUsers.usersTable.search')} placeholder={t('manageUsers.usersTable.search')} value={searchQuery} onChange={setSearchQuery} styles={" Za171 Zot171"}/>)} toolbarEnd={toolbarEnd} emptyStateTitle={t('manageUsers.usersTable.emptyTitle')} emptyStateDescription={emptyDescription}/>
        </div>

        {footnote}
      </div>);
});
OrgUsersTable.displayName = 'OrgUsersTable';

import "macro-c8192579c3fcf9ce28f716914ac6aee5c478a7952189343876b9a00274001063.css";
import "macro-6f301e34ce13f5f8ce6d6f16389e0abd645a51eef3f40ec07372c8bc8074f6f4.css";
import "macro-e0331648be6fa369228bb62bb4607a9a989af171fa57111f00917f0a3de3b34c.css";
import "macro-93ad0e565f5414135416a51f80678f2fee1bcb191dc1b8f91f86be1374321bdd.css";